import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Exporta el panel a Excel con las mismas columnas que se ven en pantalla,
 * una hoja por seccion. Se arma en el navegador porque el panel ya esta cargado:
 * no hace falta volver a pedirlo al back.
 *
 * La libreria xlsx del proyecto es la version community, que no escribe estilos
 * (bordes, fondos, ajuste de texto). Por eso lo que se replica es la estructura;
 * dentro de una celda que en pantalla ocupa varias lineas, los datos van
 * separados por punto medio para que se lean sin ajuste de texto.
 */

const SEP = ' · ';

/** Ancho de columna en caracteres, acotado para que ninguna quede ilegible ni enorme. */
const anchos = (filas, columnas) =>
  columnas.map((c) => {
    const largos = filas.map((f) => String(f[c] ?? '').length);
    const mayor = Math.max(c.length, ...(largos.length ? largos : [0]));
    return { wch: Math.min(Math.max(mayor + 2, 12), 80) };
  });

const agregarHoja = (libro, nombre, filas) => {
  if (!filas || filas.length === 0) return;
  const columnas = Object.keys(filas[0]);
  const hoja = XLSX.utils.json_to_sheet(filas, { header: columnas });
  hoja['!cols'] = anchos(filas, columnas);
  hoja['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { c: 0, r: 0 },
      e: { c: columnas.length - 1, r: filas.length },
    }),
  };
  // Deja fija la fila de encabezado al desplazarse.
  hoja['!freeze'] = { xSplit: 0, ySplit: 1 };
  // Excel corta los nombres de hoja en 31 caracteres y rechaza algunos simbolos.
  XLSX.utils.book_append_sheet(
    libro,
    hoja,
    nombre.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)
  );
};

const unir = (...partes) => partes.filter(Boolean).join(SEP);

export const exportarPanelAExcel = (panel, meta, t) => {
  const libro = XLSX.utils.book_new();
  const enc = panel?.encabezado ?? {};

  // --- Resumen ---
  agregarHoja(libro, t('ap_xlsSheetSummary'), [
    { [t('ap_xlsField')]: t('ap_title'), [t('ap_xlsValue')]: enc.titulo ?? '' },
    { [t('ap_xlsField')]: t('ap_xlsLoad'), [t('ap_xlsValue')]: enc.carga ?? '' },
    { [t('ap_xlsField')]: t('ap_xlsReviewDate'), [t('ap_xlsValue')]: enc.fecha_revision ?? '' },
    { [t('ap_xlsField')]: t('ap_window'), [t('ap_xlsValue')]: unir(enc.ventana_cierre_desde, enc.ventana_cierre_hasta) },
    { [t('ap_xlsField')]: t('ap_generatedAt'), [t('ap_xlsValue')]: panel?.generado_en ?? '' },
    { [t('ap_xlsField')]: t('ap_xlsContract'), [t('ap_xlsValue')]: panel?.version ?? meta?.schemaVersion ?? '' },
    { [t('ap_xlsField')]: t('ap_methodology'), [t('ap_xlsValue')]: enc.base ?? '' },
    { [t('ap_xlsField')]: t('ap_xlsPriority'), [t('ap_xlsValue')]: enc.criterio_priorizacion ?? '' },
  ]);

  // --- 1. Alertas de compliance: Origen | Que se dijo | Accion de hoy ---
  const correccionPorTipo = {};
  (panel?.compliance?.tipologia_riesgo ?? []).forEach((x) => {
    correccionPorTipo[x.tipo] = x.correccion_instruida;
  });

  agregarHoja(
    libro,
    t('ap_xlsSheetCompliance'),
    (panel?.anexo_a_casos_criticos?.casos ?? []).map((k) => {
      const mismo =
        String(k.consultor ?? '').trim() === String(k.responsable ?? '').trim();
      return {
        [t('ap_colOrigin')]: unir(
          `${t('ap_deal')} ${k.deal_id}`,
          k.consultor,
          mismo ? null : `${t('ap_saidBy')} ${k.responsable}`,
          k.servicio,
          k.fecha
        ),
        [t('ap_colSaid')]: unir(k.cita, k.tipo),
        [t('ap_colAction')]:
          correccionPorTipo[k.tipo] ?? panel?.compliance?.accion_critico ?? '',
      };
    })
  );

  // --- 2. Recuperables urgentes: Consultor/negocio | Cliente/servicio | Ventana | Accion ---
  agregarHoja(
    libro,
    t('ap_xlsSheetUrgent'),
    (panel?.recuperables_urgentes?.casos ?? []).map((k) => ({
      [t('ap_colConsultantDeal')]: unir(
        k.consultor,
        `${t('ap_deal')} ${k.deal_id}`
      ),
      [t('ap_colClientService')]: unir(k.cliente, k.servicio, k.telefono),
      [t('ap_colWindow')]: unir(
        k.fecha_limite ? `${t('ap_limitDate')}: ${k.fecha_limite}` : null,
        k.senal_urgencia
      ),
      [t('ap_colAction')]: k.accion_hoy ?? '',
    }))
  );

  // --- 3. Correcciones de protocolo: Consultor | Paso incumplido | Coaching ---
  const playbookPorClave = {};
  (panel?.correcciones_protocolo?.playbooks ?? []).forEach((b) => {
    playbookPorClave[b.clave] = b;
  });

  agregarHoja(
    libro,
    t('ap_xlsSheetProtocol'),
    (panel?.correcciones_protocolo?.asignacion_por_consultor ?? []).map((k) => ({
      [t('ap_colConsultant')]: k.consultor ?? '',
      [t('ap_colStep')]: unir(k.titulo, k.justificacion),
      [t('ap_colCoaching')]:
        playbookPorClave[k.clave]?.correccion_instruida ?? '',
    }))
  );

  // --- 4. Checklist: Consultor | Tareas de hoy | Coaching | Esta semana ---
  agregarHoja(
    libro,
    t('ap_xlsSheetChecklist'),
    (panel?.checklist_diario?.consultores ?? []).map((k) => {
      const tareas = [
        (k.compliance_deal_ids ?? []).length > 0
          ? unir(
              `${t('ap_complianceTag').toUpperCase()} (${k.compliance_casos})`,
              k.nota_compliance,
              k.compliance_deal_ids.join(', ')
            )
          : null,
        (k.recuperables_urgentes_deal_ids ?? []).length > 0
          ? unir(
              `${t('ap_resumeToday')} (${k.recuperables_urgentes_casos})`,
              k.recuperables_urgentes_deal_ids.join(', ')
            )
          : null,
        (k.verificar_crm_deal_ids ?? []).length > 0
          ? unir(
              `${t('ap_fixInCrm')} (${k.verificar_crm_casos})`,
              k.nota_crm,
              k.verificar_crm_deal_ids.join(', ')
            )
          : null,
      ].filter(Boolean);

      return {
        [t('ap_colConsultant')]: unir(
          k.consultor,
          `${k.negocios_perdidos} ${t('ap_lost')}`,
          `${k.negocios_evitables} ${t('ap_avoidable')}`,
          `${t('ap_closeScore')} ${k.score_cierre_prom}/4`,
          `${t('ap_followScore')} ${k.score_seguimiento_prom}/4`
        ),
        [t('ap_colTodayTasks')]: tareas.length
          ? tareas.join(' | ')
          : t('ap_noTasks'),
        [t('ap_coaching')]: unir(k.coaching_paso_protocolo, k.coaching_detalle),
        [t('ap_thisWeek')]: k.exigencia_semana ?? '',
      };
    })
  );

  const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], { type: 'application/octet-stream' });

  const fecha = (enc.fecha_revision || meta?.fecha || '').replace(/\//g, '-');
  saveAs(blob, `Panel_Autopsia_${fecha || 'panel'}.xlsx`);
};
